import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { deduplicateRequest, createRequestKey } from '../../utils/requestDeduplication';

const API_URL = '/api';

// Función auxiliar para formatear fechas correctamente
function formatDateCorrectly(dateString) {
  if (!dateString) return 'N/A';
  
  // Si la fecha ya tiene formato ISO con T (viene del servidor)
  if (typeof dateString === 'string' && dateString.includes('T')) {
    // Extraer solo la parte YYYY-MM-DD y formatearla manualmente
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  }
  
  // Si es una fecha simple YYYY-MM-DD
  if (typeof dateString === 'string' && dateString.includes('-') && dateString.length === 10) {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }
  
  // Fallback: intentar como mejor podamos
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
  } catch (e) {
    console.error("Error formateando fecha:", e);
  }
  
  return 'N/A';
}

export const useAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [showAssignProfileForm, setShowAssignProfileForm] = useState(false);
  const [showEditProfileForm, setShowEditProfileForm] = useState(false);
  const [newAccount, setNewAccount] = useState({ 
    serviceId: 1, 
    username: '', 
    password: '', 
    observation: '',
    profiles: [{ name: '', status: 'libre' }] 
  });
  const [profileAssignment, setProfileAssignment] = useState({ 
    accountId: null,
    profileId: null,
    profileName: '',
    clientId: '', 
    pin: '', 
    expiryDate: new Date(Date.now() + 30*24*60*60*1000).toISOString().slice(0, 10),
    serviceName: '',
    servicePrice: 0,
    customPrice: null
  });
  const [editingProfile, setEditingProfile] = useState({
    id: null,
    accountId: null,
    name: '',
    status: '',
    clientId: '',
    pin: '',
    expiryDate: '',
    color: '#3b82f6',
    observations: ''
  });
  const [currentAccountPage, setCurrentAccountPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [showClientResults, setShowClientResults] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [tooltipInfo, setTooltipInfo] = useState({ show: false, content: null, position: { x: 0, y: 0 } });
  const [hiddenAccounts, setHiddenAccounts] = useState([]);
  const [showHiddenAccounts, setShowHiddenAccounts] = useState(false);
  const [serviceFilter, setServiceFilter] = useState('');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10
  });
  
  const { csrfToken, refreshCsrfToken } = useAuth();

  // Cargar cuentas ocultas desde localStorage al iniciar
  useEffect(() => {
    const savedHiddenAccounts = localStorage.getItem('hiddenAccounts');
    if (savedHiddenAccounts) {
      setHiddenAccounts(JSON.parse(savedHiddenAccounts));
    }
  }, []);

  const ensureFreshCsrfToken = async () => {
    try {
      const result = await refreshCsrfToken();
      if (!result.success) {
        console.error('Error al refrescar el token CSRF');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error al refrescar el token CSRF:', error);
      return false;
    }
  };

  const fetchAccounts = useCallback(async (page = currentAccountPage, limit = itemsPerPage) => {
    // Crear clave única basada en todos los parámetros para deduplicación
    const requestKey = createRequestKey('fetchAccounts', {
      page,
      limit,
      searchTerm,
      serviceFilter,
      expiryFilter
    });
    
    return deduplicateRequest(requestKey, async () => {
      try {
        setLoading(true);
        
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString()
        });
        
        // Aplicar filtros en la petición
        if (searchTerm) {
          params.append('search', searchTerm);
        }
        if (serviceFilter) {
          params.append('serviceId', serviceFilter);
        }
        // Agregar el filtro de estado
        if (expiryFilter && expiryFilter !== 'all') {
          params.append('expiryFilter', expiryFilter);
        }
        
        const response = await axios.get(`${API_URL}/accounts?${params.toString()}`);
        
        // Manejar la respuesta paginada
        if (response.data.accounts && response.data.pagination) {
          setAccounts(response.data.accounts);
          setPaginationInfo(response.data.pagination);
        } else {
          // Fallback para respuesta sin paginación
          setAccounts(response.data);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error:', err);
        setError('Error al cargar las cuentas. Por favor, intente de nuevo.');
      } finally {
        setLoading(false);
      }
    });
  }, [currentAccountPage, itemsPerPage, searchTerm, serviceFilter, expiryFilter]);

  const handleSaveAccount = async () => {
    try {
      setLoading(true);
      
      if (!await ensureFreshCsrfToken()) {
        setError('Error al obtener un token CSRF válido. Intente recargar la página.');
        setLoading(false);
        return;
      }
      
      const normalizedProfiles = newAccount.profiles.map(profile => {
        if (profile.status === 'ocupado' && !profile.expiryDate && !profile.expiry_date) {
          const defaultExpiryDate = new Date(Date.now() + 30*24*60*60*1000).toISOString().slice(0, 10);
          return {
            ...profile,
            expiryDate: defaultExpiryDate,
            expiry_date: defaultExpiryDate
          };
        }
        return profile;
      });
      
      const accountData = {
        serviceId: parseInt(newAccount.serviceId),
        username: newAccount.username,
        password: newAccount.password,
        observation: newAccount.observation,
        profiles: normalizedProfiles.map(profile => ({
          name: profile.name,
          status: profile.status,
          clientId: profile.clientId ? parseInt(profile.clientId) : null,
          pin: profile.pin || null,
          expiryDate: profile.expiryDate || profile.expiry_date || null,
          expiry_date: profile.expiry_date || profile.expiryDate || null
        }))
      };
  
      if (newAccount.id) {
        await axios.put(`${API_URL}/accounts/${newAccount.id}`, accountData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      } else {
        await axios.post(`${API_URL}/accounts`, accountData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      }
  
      await fetchAccounts();
      setNewAccount({ serviceId: '', username: '', password: '', observation: '', profiles: [{ name: '', status: 'libre' }] });
      setShowAccountForm(false);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al guardar la cuenta. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta cuenta?')) {
      try {
        setLoading(true);
        
        if (!await ensureFreshCsrfToken()) {
          setError('Error al obtener un token CSRF válido. Intente recargar la página.');
          setLoading(false);
          return;
        }
        
        await axios.delete(`${API_URL}/accounts/${accountId}`, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });

        await fetchAccounts();
        setError(null);
      } catch (err) {
        console.error('Error:', err);
        setError('Error al eliminar la cuenta. Por favor, intente de nuevo.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditAccount = (account) => {
    setNewAccount({
      id: account.id,
      serviceId: account.service_id || account.serviceId,
      username: account.username,
      password: account.password,
      observation: account.observation || '',
      profiles: account.profiles.map(profile => ({
        id: profile.id,
        name: profile.name,
        status: profile.status,
        clientId: profile.client_id || profile.clientId,
        pin: profile.pin || '',
        expiryDate: profile.expiry_date || profile.expiryDate || '',
        expiry_date: profile.expiry_date || profile.expiryDate || ''
      }))
    });
    setShowAccountForm(true);
  };

  const handleAssignProfile = (account, profile, services) => {
    const service = services.find(s => s.id === parseInt(account.service_id || account.serviceId));
    
    const defaultExpiryDate = new Date(Date.now() + 30*24*60*60*1000).toISOString().slice(0, 10);
    
    setProfileAssignment({
      accountId: account.id,
      profileId: profile.id,
      profileName: profile.name,
      clientId: '',
      pin: '',
      expiryDate: defaultExpiryDate,
      serviceName: service ? service.name : '',
      servicePrice: service ? parseFloat(service.price) : 0,
      customPrice: null
    });
    
    setClientSearchQuery('');
    setShowAssignProfileForm(true);
  };

  const handleEditProfile = (account, profile) => {
    if (profile.status === 'ocupado') {
      let expiryDate = profile.expiryDate || profile.expiry_date || new Date().toISOString().slice(0, 10);
      
      // Convertir la fecha al formato YYYY-MM-DD requerido por el input type="date"
      if (expiryDate && expiryDate.includes('T')) {
        // Si la fecha tiene formato ISO con T (viene del servidor)
        expiryDate = expiryDate.split('T')[0];
      }
      
      setEditingProfile({
        id: profile.id,
        accountId: account.id,
        name: profile.name,
        status: profile.status,
        clientId: profile.client_id || profile.clientId,
        pin: profile.pin,
        expiryDate: expiryDate,
        expiry_date: expiryDate,
        color: profile.color || null,
        observations: profile.observations || ''
      });
      
      setShowEditProfileForm(true);
    } else {
      alert(`Este perfil está ${profile.status}. Solo se pueden editar perfiles ocupados.`);
    }
  };

  const handleSaveProfileAssignment = async (services, clients, movements, getServiceTemplate) => {
    try {
      setLoading(true);
      
      if (!await ensureFreshCsrfToken()) {
        setError('Error al obtener un token CSRF válido. Intente recargar la página.');
        setLoading(false);
        return;
      }
      
      const accountToUpdate = accounts.find(a => a.id === profileAssignment.accountId);
      
      if (!accountToUpdate) {
        throw new Error('Cuenta no encontrada');
      }
      
      // Datos del perfil a actualizar
      const profileData = {
        name: profileAssignment.profileName,
        status: 'ocupado',
        clientId: parseInt(profileAssignment.clientId),
        pin: profileAssignment.pin || null,
        expiryDate: profileAssignment.expiryDate,
        color: null, // Para asignación nueva, no se especifica color
        observations: null // Para asignación nueva, no se especifican observaciones
      };
      
      console.log('Asignando perfil con datos:', profileData);
      
      // Usar el nuevo endpoint optimizado para actualizar solo este perfil
      const response = await axios.put(`${API_URL}/profiles/${profileAssignment.profileId}`, profileData, {
        headers: {
          'X-CSRF-Token': csrfToken
        }
      });
      
      // Actualizar el estado local con el perfil actualizado
      const updatedProfile = response.data;
      
      setAccounts(prevAccounts => 
        prevAccounts.map(account => {
          if (account.id === profileAssignment.accountId) {
            return {
              ...account,
              profiles: account.profiles.map(profile => 
                profile.id === profileAssignment.profileId ? updatedProfile : profile
              )
            };
          }
          return account;
        })
      );
      
      // Crear movimiento de venta
      const finalPrice = profileAssignment.customPrice || profileAssignment.servicePrice;
      const movementData = {
        date: new Date().toISOString().slice(0, 10),
        type: 'venta',
        amount: finalPrice,
        clientId: parseInt(profileAssignment.clientId),
        serviceId: parseInt(accountToUpdate.service_id || accountToUpdate.serviceId),
        description: `Venta de perfil "${profileAssignment.profileName}" del servicio "${profileAssignment.serviceName}". Precio: $${finalPrice.toLocaleString('es-CO')}. Vence: ${formatDateCorrectly(profileAssignment.expiryDate)}`
      };
      
      await axios.post(`${API_URL}/movements`, movementData, {
        headers: {
          'X-CSRF-Token': csrfToken
        }
      });
      
      // Copiar información al portapapeles
      const account = accounts.find(a => a.id === profileAssignment.accountId);
      const service = services.find(s => s.id === parseInt(account.service_id || account.serviceId));
      const replacements = {
        nombre_servicio: service.name || 'N/A',
        correo: account.username || 'N/A',
        contraseña: account.password || 'N/A',
        perfil: profileAssignment.profileName || 'N/A',
        pin: profileAssignment.pin || 'N/A',
        fecha_vencimiento: formatDateCorrectly(profileAssignment.expiryDate)
      };
      
      const templateText = getServiceTemplate(service.id);
      const infoToCopy = safeReplaceTemplateVars(templateText, replacements);

      await navigator.clipboard.writeText(infoToCopy);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);

      setShowAssignProfileForm(false);
      setError(null);
      console.log('Perfil asignado exitosamente sin recargar toda la página');
      
    } catch (err) {
      console.error('Error al asignar perfil:', err);
      setError('Error al asignar el perfil. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEditedProfile = async () => {
    try {
      setLoading(true);
      
      if (!await ensureFreshCsrfToken()) {
        setError('Error al obtener un token CSRF válido. Intente recargar la página.');
        setLoading(false);
        return;
      }
      
      // Asignar fecha por defecto si está ocupado pero no tiene fecha
      if (editingProfile.status === 'ocupado' && !editingProfile.expiryDate && !editingProfile.expiry_date) {
        const defaultExpiryDate = new Date(Date.now() + 30*24*60*60*1000).toISOString().slice(0, 10);
        editingProfile.expiryDate = defaultExpiryDate;
        editingProfile.expiry_date = defaultExpiryDate;
      }
      
      // Datos del perfil a actualizar
      const profileData = {
        name: editingProfile.name,
        status: editingProfile.status,
        clientId: editingProfile.status === 'ocupado' ? parseInt(editingProfile.clientId) || null : null,
        pin: editingProfile.pin || null,
        expiryDate: editingProfile.expiryDate || editingProfile.expiry_date || null,
        color: editingProfile.color || null,
        observations: editingProfile.observations || null
      };
      
      console.log('Enviando datos del perfil:', profileData);
      
      // Usar el nuevo endpoint optimizado para actualizar solo este perfil
      const response = await axios.put(`${API_URL}/profiles/${editingProfile.id}`, profileData, {
        headers: {
          'X-CSRF-Token': csrfToken
        }
      });
      
      // Actualizar el estado local con el perfil actualizado
      const updatedProfile = response.data;
      
      setAccounts(prevAccounts => 
        prevAccounts.map(account => {
          if (account.id === editingProfile.accountId) {
            return {
              ...account,
              profiles: account.profiles.map(profile => 
                profile.id === editingProfile.id ? updatedProfile : profile
              )
            };
          }
          return account;
        })
      );
      
      setShowEditProfileForm(false);
      setError(null);
      console.log('Perfil actualizado exitosamente sin recargar toda la página');
      
    } catch (err) {
      console.error('Error al actualizar perfil:', err);
      setError('Error al editar el perfil. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };



  const handleAddProfile = () => {
    setNewAccount({
      ...newAccount,
      profiles: [...newAccount.profiles, { name: '', status: 'libre' }]
    });
  };

  const handleProfileChange = (index, field, value) => {
    const updatedProfiles = [...newAccount.profiles];
    updatedProfiles[index] = { ...updatedProfiles[index], [field]: value };
    setNewAccount({ ...newAccount, profiles: updatedProfiles });
  };

  const handleServiceChange = (serviceId, services) => {
    const service = services.find(s => s.id === parseInt(serviceId));
    
    if (service) {
      const maxProfiles = service.max_profiles || service.maxProfiles || 0;
      
      const profiles = Array.from({ length: maxProfiles }, (_, index) => ({
        name: `Perfil ${index + 1}`,
        status: 'libre'
      }));
      
      setNewAccount({
        ...newAccount,
        serviceId,
        profiles,
        observation: newAccount.observation
      });
    } else {
      setNewAccount({
        ...newAccount,
        serviceId,
        profiles: [{ name: '', status: 'libre' }],
        observation: newAccount.observation
      });
    }
  };

  const isProfileNearExpiry = (profile) => {
    if (!profile.expiryDate && !profile.expiry_date) return false;
    
    const expiryDate = new Date(profile.expiryDate || profile.expiry_date);
    const today = new Date();
    
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 && diffDays <= 5;
  };

  const isProfileExpired = (profile) => {
    if (!profile.expiryDate && !profile.expiry_date) return false;
    
    const expiryDate = new Date(profile.expiryDate || profile.expiry_date);
    const today = new Date();
    
    return expiryDate < today;
  };

  const filterClients = (query, clients) => {
    if (!query) {
      setFilteredClients([]);
      setShowClientResults(false);
      return;
    }

    const lowerQuery = query.toLowerCase();

    const filtered = clients.filter(client => {
      const nameMatch = client.name?.toLowerCase().includes(lowerQuery);
      const phoneMatch = client.phone?.toLowerCase().includes(lowerQuery);
      return nameMatch || phoneMatch;
    }); // ✅ Sin límite - muestra todos los clientes que coincidan

    setFilteredClients(filtered);
    setShowClientResults(true);
  };

  const handleClientSearch = (e, clients) => {
    const query = e.target.value;
    setClientSearchQuery(query);

    // Buscar inmediatamente mientras escribe
    filterClients(query, clients);
  };

  const handleSelectClient = (client) => {
    setProfileAssignment({
      ...profileAssignment,
      clientId: client.id
    });
    setClientSearchQuery(`${client.name} (${client.phone || 'Sin teléfono'})`);
    setShowClientResults(false);
  };

  const getFilteredAccounts = (services) => {
    let filtered = accounts;
    
    // Los filtros de estado ahora se aplican en el servidor via SQL
    // Solo mantenemos el filtrado local para cuentas ocultas
    
    // Aplicar filtrado de cuentas ocultas
    if (showHiddenAccounts) {
      // Cuando está activado, mostrar SOLO las cuentas ocultas
      filtered = filtered.filter(account => hiddenAccounts.includes(account.id));
    } else {
      // Cuando está desactivado, mostrar solo las cuentas NO ocultas
      filtered = filtered.filter(account => !hiddenAccounts.includes(account.id));
    }
    
    // Ordenamiento usando las funciones del cliente
    filtered.sort((a, b) => {
      const aPriority = a.profiles && a.profiles.some(p => isProfileExpired(p)) ? 3 : 
                       a.profiles && a.profiles.some(p => isProfileNearExpiry(p)) ? 2 : 1;
      
      const bPriority = b.profiles && b.profiles.some(p => isProfileExpired(p)) ? 3 : 
                       b.profiles && b.profiles.some(p => isProfileNearExpiry(p)) ? 2 : 1;
      
      return bPriority - aPriority;
    });
    
    return filtered;
  };

  const getClientName = (clientId, clients) => {
    if (!clientId) return 'N/A';
    
    if (!clients || !Array.isArray(clients) || clients.length === 0) {
      return 'N/A';
    }
    
    // Intentar múltiples formas de comparación
    const client = clients.find(c => {
      return c.id === clientId || c.id === parseInt(clientId) || c.id === String(clientId);
    });
    
    return client ? client.name : 'N/A';
  };

  const getPaginatedAccounts = (services) => {
    // Como los datos ya vienen paginados del servidor, 
    // solo necesitamos aplicar los filtros
    return getFilteredAccounts(services);
  };

  const getServiceName = (serviceId, services) => {
    if (!serviceId) return 'N/A';
    const service = services.find(s => s.id === parseInt(serviceId));
    return service ? service.name : 'N/A';
  };

  const showProfileTooltip = (e, profile, account, serviceName, clients) => {
    e.preventDefault();
    
    // Obtener el nombre del cliente de manera robusta
    let clientName = 'N/A';
    
    if (profile.clientName) {
      clientName = profile.clientName;
    } else {
      const clientId = profile.client_id || profile.clientId;
      
      if (clientId && clients && clients.length > 0) {
        const client = clients.find(c => {
          // Intentar tanto comparación directa como con parseInt
          return c.id === clientId || c.id === parseInt(clientId);
        });
        
        if (client) {
          clientName = client.name;
        }
      }
    }
    
    const content = (
      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-3 max-w-xs">
        <div className="flex items-center gap-2 pb-2 border-b border-gray-700 mb-2">
          <span className="font-bold text-white">{profile.name}</span>
        </div>
        {profile.status === 'ocupado' && (
          <>
            <div className="text-xs text-gray-300 flex flex-col gap-1">
              <div><span className="text-gray-500">Cliente:</span> {clientName}</div>
              {profile.pin && <div><span className="text-gray-500">PIN:</span> {profile.pin}</div>}
              {(profile.expiryDate || profile.expiry_date) && (
                <div>
                  <span className="text-gray-500">Vence:</span> {formatDateCorrectly(profile.expiryDate || profile.expiry_date)}
                </div>
              )}
              {profile.observations && (
                <div>
                  <span className="text-gray-500">Observaciones:</span> {profile.observations}
                </div>
              )}
            </div>
            <button 
              className="mt-2 text-xs bg-blue-600 hover:bg-blue-700 text-white py-1 px-2 rounded flex items-center gap-1"
              onClick={() => handleEditProfile(account, profile)}
            >
              Editar perfil
            </button>
          </>
        )}
        {profile.status === 'libre' && (
          <button 
            className="mt-2 text-xs bg-green-600 hover:bg-green-700 text-white py-1 px-2 rounded flex items-center gap-1"
            onClick={() => handleAssignProfile(account, profile)}
          >
            Asignar perfil
          </button>
        )}
      </div>
    );
    
    const rect = e.currentTarget.getBoundingClientRect();
    const position = {
      x: rect.left + window.scrollX,
      y: rect.bottom + window.scrollY + 5,
    };
    
    setTooltipInfo({
      show: true,
      content,
      position
    });
  };

  const hideProfileTooltip = () => {
    setTooltipInfo({ show: false, content: null, position: { x: 0, y: 0 } });
  };

  const toggleHideAccount = (accountId) => {
    setHiddenAccounts(prev => {
      let updated;
      if (prev.includes(accountId)) {
        updated = prev.filter(id => id !== accountId);
      } else {
        updated = [...prev, accountId];
      }
      
      localStorage.setItem('hiddenAccounts', JSON.stringify(updated));
      
      return updated;
    });
  };

  const copyProfileInfo = (services, getServiceTemplate) => {
    const account = accounts.find(a => a.id === editingProfile.accountId);
    const service = services.find(s => s.id === parseInt(account.service_id || account.serviceId));
    const replacements = {
      nombre_servicio: service.name || 'N/A',
      correo: account.username || 'N/A',
      contraseña: account.password || 'N/A',
      perfil: editingProfile.name || 'N/A',
      pin: editingProfile.pin || 'N/A',
      fecha_vencimiento: formatDateCorrectly(editingProfile.expiryDate)
    };
    const templateText = getServiceTemplate(service.id);
    const infoToCopy = safeReplaceTemplateVars(templateText, replacements);
  
    navigator.clipboard.writeText(infoToCopy)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      })
      .catch(err => {
        console.error('Error al copiar al portapapeles:', err);
        alert('La información no pudo ser copiada automáticamente. Por favor, cópiela manualmente.');
      });
  };

  const safeReplaceTemplateVars = (template, replacements) => {
    if (!template) return '';
    
    return template.replace(/\{\{([^{}]+)\}\}/g, (match, key) => {
      return replacements.hasOwnProperty(key) ? replacements[key] || '' : match;
    });
  };

  const handleNextPage = useCallback(() => {
    if (currentAccountPage < paginationInfo.totalPages) {
      setCurrentAccountPage(currentAccountPage + 1);
      fetchAccounts(currentAccountPage + 1, itemsPerPage);
    }
  }, [currentAccountPage, paginationInfo.totalPages, fetchAccounts, itemsPerPage]);
  
  const handlePrevPage = useCallback(() => {
    if (currentAccountPage > 1) {
      setCurrentAccountPage(currentAccountPage - 1);
      fetchAccounts(currentAccountPage - 1, itemsPerPage);
    }
  }, [currentAccountPage, fetchAccounts, itemsPerPage]);

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentAccountPage(1);
    fetchAccounts(1, newItemsPerPage);
  };

  return {
    accounts,
    loading,
    error,
    setError,
    showAccountForm,
    setShowAccountForm,
    showAssignProfileForm,
    setShowAssignProfileForm,
    showEditProfileForm,
    setShowEditProfileForm,
    newAccount,
    setNewAccount,
    profileAssignment,
    setProfileAssignment,
    editingProfile,
    setEditingProfile,
    currentAccountPage,
    itemsPerPage,
    searchTerm,
    setSearchTerm,
    clientSearchQuery,
    setClientSearchQuery,
    filteredClients,
    setFilteredClients,
    showClientResults,
    setShowClientResults,
    copySuccess,
    setCopySuccess,
    tooltipInfo,
    setTooltipInfo,
    hiddenAccounts,
    showHiddenAccounts,
    setShowHiddenAccounts,
    paginationInfo,
    setPaginationInfo,
    serviceFilter,
    setServiceFilter,
    setCurrentAccountPage,
    expiryFilter,
    setExpiryFilter,
    fetchAccounts,
    handleSaveAccount,
    handleDeleteAccount,
    handleEditAccount,
    handleAssignProfile,
    handleEditProfile,
    handleSaveProfileAssignment,
    handleSaveEditedProfile,
    handleAddProfile,
    handleProfileChange,
    handleServiceChange,
    isProfileNearExpiry,
    isProfileExpired,
    filterClients,
    handleClientSearch,
    handleSelectClient,
    getFilteredAccounts,
    getPaginatedAccounts,
    getServiceName,
    showProfileTooltip,
    hideProfileTooltip,
    getClientName,
    toggleHideAccount,
    copyProfileInfo,
    safeReplaceTemplateVars,
    handleNextPage,
    handlePrevPage,
    handleItemsPerPageChange
  };
};
